import {
  getDownloadRapportinoManutenzioneQueryKey,
  getGetNodoPortaleQueryKey,
  getGetNodoQueryKey,
  ManutenzioneDto,
  NodoDto,
  useUpdateManutenzioni,
} from "@/api";
import FileDownload from "@/components/FileField/FileDownload";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Table from "@/elements/Table";
import Typography from "@/elements/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Tooltip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ManutenzioneDialog from "../ManutenzioneDialog";
import { portaleConfig } from "@/portaliConfig";

const columnHelper = createColumnHelper<ManutenzioneDto>();

export default function ManutenzioniTableCard({
  nodo,
  canEdit = false,
}: {
  nodo: NodoDto;
  canEdit?: boolean;
}) {
  const { t } = useTranslation();
  const [manutenzioneDialogOptions, setManutenzioneDialogOptions] = useState<{
    open: boolean;
    manutenzione?: ManutenzioneDto;
  }>({ open: false });

  const editing = canEdit;

  const queryClient = useQueryClient();
  const { mutate } = useUpdateManutenzioni();

  const manutenzioni = nodo.manutenzioni || [];

  const deleteManutenzione = useCallback(
    (manutenzione: ManutenzioneDto) => {
      const message = t("manutenzioniTable.confermaElimina");
      if (!window.confirm(message)) {
        return;
      }

      mutate(
        {
          id: nodo.id,
          data: [
            {
              id: manutenzione.id,
              changeType: "deleted",
              entity: manutenzione,
            },
          ],
        },
        {
          onSuccess: () => {
            queryClient
              .invalidateQueries(getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id))
              .then(() => {
                toast.success(t("manutenzioniTable.toast.eliminata"));
              });
          },
          onError: () => {
            toast.error(t("manutenzioniTable.toast.erroreEliminazione"));
          },
        }
      );
    },
    [queryClient, nodo, manutenzioni, mutate, t]
  );

  const columns = useMemo<ColumnDef<ManutenzioneDto, any>[]>(() => {
    const columns = [
      columnHelper.accessor("data", {
        header: t("manutenzioniTable.colonne.data"),
        cell: ({ row }) => moment(row.original.data).format("L"),
      }),

      columnHelper.accessor("descrizione", {
        header: t("manutenzioniTable.colonne.descrizione"),
        minSize: 200,
      }),

      columnHelper.accessor("scadenza", {
        header: t("manutenzioniTable.colonne.scadenza"),
        cell: ({ row }) =>
          row.original.scadenza ? moment(row.original.scadenza).format("L") : "",
      }),

      columnHelper.display({
        header: t("manutenzioniTable.colonne.rapportino"),
        cell: ({ row }) => (
          <FileDownload
            file={row.original.fileRapportino}
            downloadUrl={
              getDownloadRapportinoManutenzioneQueryKey(
                nodo.id,
                row.original.id
              )[0]
            }
          />
        ),
      }),

      editing &&
        columnHelper.display({
          id: "buttons",
          header: "",
          size: 100,
          maxSize: 100,
          cell: ({ row }) => {
            return (
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="flex-end"
                gap={1}
              >
                <Tooltip title={t("azioni.elimina")}>
                  <IconButton
                    onClick={() => {
                      deleteManutenzione(row.original);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title={t("azioni.modifica")}>
                  <IconButton
                    onClick={() => {
                      setManutenzioneDialogOptions({
                        open: true,
                        manutenzione: row.original,
                      });
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          },
        }),
    ].filter((x) => x);
    return columns as Exclude<(typeof columns)[number], boolean>[];
  }, [editing, nodo, manutenzioni, deleteManutenzione, t]);

  return (
    <>
      <Box flex={1}>
        <Box display="flex" flexDirection="column" height="100%" gap={1}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            gap={1}
          >
            <Typography variant="h5" color="text" fontWeight="medium">
              {t("manutenzioniTable.titolo")}
            </Typography>

            {editing && (
              <Button
                color="primary"
                size="small"
                onClick={() => {
                  setManutenzioneDialogOptions({ open: true });
                }}
              >
                {t("azioni.aggiungi")}
              </Button>
            )}
          </Box>

          <Table
            columns={columns}
            data={manutenzioni}
            initialSort={[{ id: "id", desc: true }]}
            noRowsText={t("manutenzioniTable.vuoto")}
            mt={1}
          />
        </Box>
      </Box>

      <ManutenzioneDialog
        nodo={nodo}
        manutenzione={manutenzioneDialogOptions.manutenzione}
        open={manutenzioneDialogOptions.open}
        onClose={() => {
          setManutenzioneDialogOptions({ open: false });
        }}
      />
    </>
  );
}
